import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { safeFormatDate } from '../shared/utils';
import apiCall from '../shared/apiCall';

export default function ShareToGroupModal({
  show,
  onHide,
  parsedData,
  exclusions,
  setExclusions,
  expandedSections,
  setExpandedSections,
  selectedGroupIds,
  setSelectedGroupIds,
  shareOptions,
  setShareOptions,
  emailNotification,
  setEmailNotification,
  onProceed,
  isSharing,
  message,
  onClearMessage,
  accountSettings = {}
}) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null); // { url, name } or null
  const modalBodyRef = useRef(null);
  // Collect all transportation and accommodation activities
  const transportationActivities = [];
  const accommodationActivities = [];
  
  if (parsedData?.days) {
    parsedData.days.forEach((day, dayIndex) => {
      if (day.activities) {
        day.activities.forEach(activity => {
          if (activity.type === 'transportation' && activity.id) {
            transportationActivities.push({
              ...activity,
              dayIndex: dayIndex + 1,
              dayDate: day.date
            });
          } else if (activity.type === 'accommodation' && activity.id) {
            accommodationActivities.push({
              ...activity,
              dayIndex: dayIndex + 1,
              dayDate: day.date
            });
          }
        });
      }
    });
  }

  const handleActivityToggle = (activityId, checked) => {
    // Convert to string for consistent comparison
    const activityIdStr = String(activityId);
    const currentExcluded = (exclusions.excludedActivityIds || []).map(id => String(id));
    if (checked) {
      // Checked = include: remove from excluded list
      setExclusions(prev => ({
        ...prev,
        excludedActivityIds: prev.excludedActivityIds.filter(id => String(id) !== activityIdStr)
      }));
    } else {
      // Unchecked = exclude: add to excluded list
      if (!currentExcluded.includes(activityIdStr)) {
        setExclusions(prev => ({
          ...prev,
          excludedActivityIds: [...prev.excludedActivityIds, activityIdStr]
        }));
      }
    }
  };

  // Fetch groups when modal opens; load saved banner from plan if available via plan prop
  useEffect(() => {
    if (show) {
      fetchGroups();
      // Reset banner when opening (owner chooses per share)
      setSelectedBanner(null);
    }
  }, [show]);

  // Auto-scroll to top when message appears
  useEffect(() => {
    if (message && message.text && modalBodyRef.current) {
      // Scroll to top of modal body smoothly
      modalBodyRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [message]);

  const fetchGroups = async () => {
    setIsLoadingGroups(true);
    try {
      const response = await apiCall('/api/shared?action=GET_GROUPS', {}, 'POST');
      if (response.success) {
        setGroups(response.groups || []);
      } else {
        setGroups([]);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroups([]);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const handleClose = () => {
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-people me-2"></i>
          {t('planDetail.shareToGroups.title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body ref={modalBodyRef} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Success/Error Message */}
        {message && message.text && (
          <Alert 
            variant={message.type === 'success' ? 'success' : 'danger'} 
            className="mb-3"
            dismissible
            onClose={onClearMessage}
          >
            {message.type === 'success' && <i className="bi bi-check-circle me-2"></i>}
            {message.type === 'error' && <i className="bi bi-exclamation-circle me-2"></i>}
            {message.text}
          </Alert>
        )}
        
        <p className="mb-4">
          {t('planDetail.shareToGroups.description')}
        </p>
        
        {/* Show message if no groups exist */}
        {!isLoadingGroups && groups.length === 0 && (
          <Alert variant="info" className="mb-4">
            <i className="bi bi-info-circle me-2"></i>
            {t('planDetail.shareToGroups.noGroupsMessage')}
          </Alert>
        )}
        
        {/* Activity Inclusions Section */}
        {(transportationActivities.length > 0 || accommodationActivities.length > 0) && (
          <div className="mb-4">
            <h6 className="mb-3">{t('planDetail.shareToGroups.includeActivities')}</h6>
            
            {/* Transportation Section */}
            {transportationActivities.length > 0 && (
            <div className="mb-3">
              <div 
                className="d-flex align-items-center justify-content-between p-2 bg-light rounded cursor-pointer"
                style={{ cursor: 'pointer' }}
                onClick={() => setExpandedSections(prev => ({
                  ...prev,
                  transportation: !prev.transportation
                }))}
              >
                <div className="d-flex align-items-center">
                  <span className="route-icon me-2"></span>
                  <strong>{t('planDetail.shareToGroups.transportation')} ({transportationActivities.length})</strong>
                </div>
                <i className={`bi bi-chevron-${expandedSections.transportation ? 'up' : 'down'}`}></i>
              </div>
              
              {expandedSections.transportation && (
                <div className="ms-3 mt-2 mb-2 border-start ps-3" style={{ borderColor: '#dee2e6', borderWidth: '2px' }}>
                  {transportationActivities.map((activity) => (
                    <div key={activity.id} className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`include-group-transportation-${activity.id}`}
                        checked={!exclusions?.excludedActivityIds || exclusions.excludedActivityIds.length === 0 || !exclusions.excludedActivityIds.map(id => String(id)).includes(String(activity.id))}
                        onChange={(e) => handleActivityToggle(activity.id, e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor={`include-group-transportation-${activity.id}`}>
                        <div>
                          <strong>{activity.name}</strong>
                          {activity.dayDate && (
                            <small className="text-muted ms-2">
                              ({t('planDetail.shareToGroups.day')} {activity.dayIndex} - {safeFormatDate(activity.dayDate, 'MMM d')})
                            </small>
                          )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Accommodation Section */}
          {accommodationActivities.length > 0 && (
            <div className="mb-3">
              <div 
                className="d-flex align-items-center justify-content-between p-2 bg-light rounded cursor-pointer"
                style={{ cursor: 'pointer' }}
                onClick={() => setExpandedSections(prev => ({
                  ...prev,
                  accommodation: !prev.accommodation
                }))}
              >
                <div className="d-flex align-items-center">
                  <i className="bi bi-building me-2"></i>
                  <strong>{t('planDetail.shareToGroups.accommodation')} ({accommodationActivities.length})</strong>
                </div>
                <i className={`bi bi-chevron-${expandedSections.accommodation ? 'up' : 'down'}`}></i>
              </div>
              
              {expandedSections.accommodation && (
                <div className="ms-3 mt-2 mb-2 border-start ps-3" style={{ borderColor: '#dee2e6', borderWidth: '2px' }}>
                  {accommodationActivities.map((activity) => (
                    <div key={activity.id} className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`include-group-accommodation-${activity.id}`}
                        checked={!exclusions?.excludedActivityIds || exclusions.excludedActivityIds.length === 0 || !exclusions.excludedActivityIds.map(id => String(id)).includes(String(activity.id))}
                        onChange={(e) => handleActivityToggle(activity.id, e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor={`include-group-accommodation-${activity.id}`}>
                        <div>
                          <strong>{activity.name}</strong>
                          {activity.dayDate && (
                            <small className="text-muted ms-2">
                              ({t('planDetail.shareToGroups.day')} {activity.dayIndex} - {safeFormatDate(activity.dayDate, 'MMM d')})
                            </small>
                          )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </div>
        )}
        
        {/* Share Options Section */}
        <div className="mb-4">
          <h6 className="mb-3">{t('planDetail.shareToGroups.whatToInclude')}</h6>
          <div className="ms-2">
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="include-todos"
                checked={shareOptions?.includeTodos ?? true}
                onChange={(e) => setShareOptions(prev => ({ ...prev, includeTodos: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="include-todos">
                <strong>{t('planDetail.shareToGroups.todos')}</strong>
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="include-costs"
                checked={shareOptions?.includeCosts ?? false}
                onChange={(e) => setShareOptions(prev => ({ ...prev, includeCosts: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="include-costs">
                <strong>{t('planDetail.shareToGroups.costs')}</strong>
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="include-notes"
                checked={shareOptions?.includeNotes ?? false}
                onChange={(e) => setShareOptions(prev => ({ ...prev, includeNotes: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="include-notes">
                <strong>{t('planDetail.shareToGroups.notes')}</strong>
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="include-packing-list"
                checked={shareOptions?.includePackingList ?? true}
                onChange={(e) => setShareOptions(prev => ({ ...prev, includePackingList: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="include-packing-list">
                <strong>{t('planDetail.shareToGroups.packingList')}</strong>
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="include-info"
                checked={shareOptions?.includeInfo ?? true}
                onChange={(e) => setShareOptions(prev => ({ ...prev, includeInfo: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="include-info">
                <strong>{t('planDetail.shareToGroups.infoTab')}</strong>
              </label>
            </div>
          </div>
        </div>
        
        {/* Group Selection Section */}
        {!isLoadingGroups && groups.length > 0 && (
          <div className="mb-3">
            <h6 className="mb-3">{t('planDetail.shareToGroups.selectGroups')}</h6>
            <div>
              {groups.map((group) => (
                <div key={group._id} className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`group-${group._id}`}
                    checked={selectedGroupIds.includes(String(group._id))}
                    onChange={(e) => {
                      const groupIdStr = String(group._id);
                      if (e.target.checked) {
                        setSelectedGroupIds(prev => {
                          const prevStr = prev.map(id => String(id));
                          if (!prevStr.includes(groupIdStr)) {
                            return [...prev, groupIdStr];
                          }
                          return prev;
                        });
                      } else {
                        setSelectedGroupIds(prev => prev.filter(id => String(id) !== groupIdStr));
                      }
                    }}
                  />
                  <label className="form-check-label" htmlFor={`group-${group._id}`}>
                    <div>
                      <strong>{group.name}</strong>
                      <small className="text-muted ms-2">
                        ({group.emailCount || group.emails?.length || 0} email{group.emailCount !== 1 ? 's' : ''})
                      </small>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {isLoadingGroups && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>{t('planDetail.shareToGroups.loadingGroups')}</span>
          </div>
        )}
        
        {/* Email Notification Section */}
        <div className="mb-3">
          <h6 className="mb-3">
            {t('planDetail.shareToGroups.emailNotification')}:
            <i 
              className="bi bi-question-circle-fill text-primary ms-2" 
              style={{ cursor: 'help', fontSize: '1rem', opacity: 1, color: '#0d6efd' }}
              data-tooltip-id="email-notification-tooltip"
              data-tooltip-html={t('planDetail.shareToGroups.emailNotificationTooltip')}
            ></i>
            <Tooltip id="email-notification-tooltip" place="right" />
          </h6>
          <div className="ms-2">
            <Form.Check
              type="radio"
              id="email-notify-new"
              name="emailNotification"
              label={t('planDetail.shareToGroups.notifyNewEmailsOnly')}
              checked={emailNotification === 'new' || !emailNotification}
              onChange={() => setEmailNotification('new')}
              className="mb-2"
            />
            <Form.Check
              type="radio"
              id="email-notify-all"
              name="emailNotification"
              label={t('planDetail.shareToGroups.notifyAll')}
              checked={emailNotification === 'all'}
              onChange={() => setEmailNotification('all')}
              className="mb-2"
            />
            <Form.Check
              type="radio"
              id="email-notify-none"
              name="emailNotification"
              label={t('planDetail.shareToGroups.none')}
              checked={emailNotification === 'none'}
              onChange={() => setEmailNotification('none')}
              className="mb-2"
            />
          </div>
        </div>
        
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t('planDetail.shareToGroups.cancel')}
        </Button>
        <Button 
          variant="primary" 
          onClick={() => onProceed({ banner: selectedBanner })} 
          disabled={isSharing || isLoadingGroups || groups.length === 0 || selectedGroupIds.length === 0}
        >
          {isSharing ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              {t('planDetail.shareToGroups.sharing')}
            </>
          ) : (
            <>
              <i className="bi bi-people me-2"></i>
              {t('planDetail.shareToGroups.title')}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
